// @ts-check
import {useSortable} from '@dnd-kit/react/sortable'
import {SortableKeyboardPlugin} from '@dnd-kit/dom/sortable'
import {Text, FileUpload} from '@/components-v2'
import {memo, useEffect, useState} from 'react'
import './BrandItem.scss'
import {useBrandImage} from '@/containers/Content/ProductCatalog/hooks/useBrandImage'
import {getCatalogItemName, getPositionLabel} from '@/containers/Content/ProductCatalog/utils/productCatalogUtils'
import {BRAND_SIZE} from '../utils/catalogLayoutConstants'

/**
 * @param {{
 * brand: import('@/types/productCatalogTypes').ProductCategoryBrand,
 * position: number,
 * setBrands: import('react').Dispatch<import('react').SetStateAction<import('@/types/productCatalogTypes').ProductCategoryBrand[]>>,
 * disabled?: boolean,
 * onBusyChange: (id: string, busy: boolean) => void,
 * onRetentionChange: (id: string, retained: boolean) => void,
 * }} props
 */
const BrandItemComponent = ({brand, position, setBrands, disabled, onBusyChange, onRetentionChange}) => {
  const {ref, isDragging, isDropTarget} = useSortable({
    id: brand.id,
    index: position - 1,
    disabled,
    // Optimistic sorting renumbers mounted items, which loses virtualized indices.
    plugins: [SortableKeyboardPlugin],
  })
  const {handleUpload, handleDelete, uploading, error: imageError, progress} = useBrandImage({brandId: brand.id, setBrands})
  const [fileError, setFileError] = useState('')
  const error = fileError || imageError
  const brandName = getCatalogItemName(brand)

  useEffect(() => {
    if (!uploading) return
    onBusyChange(brand.id, true)
    return () => onBusyChange(brand.id, false)
  }, [brand.id, uploading, onBusyChange])

  const retained = uploading || Boolean(error)
  useEffect(() => {
    if (!retained) return
    onRetentionChange(brand.id, true)
    return () => onRetentionChange(brand.id, false)
  }, [brand.id, retained, onRetentionChange])

  return (
    <div
      ref={ref}
      data-brand-position={position}
      tabIndex={disabled ? -1 : 0}
      aria-label={brandName}
      aria-disabled={disabled}
      className={`flex flex-col items-center justify-between gap-2 rounded-lg border border-gray-400 p-2 ${disabled ? '' : 'cursor-grab touch-none'} ${isDragging ? 'opacity-50' : ''} ${isDropTarget && !isDragging ? 'ring-2 ring-inset ring-[var(--color-border-default)]' : ''}`}
      style={{width: BRAND_SIZE, minWidth: BRAND_SIZE, height: BRAND_SIZE, contain: 'layout paint'}}
    >
      <Text size="xsmall" className="display-block w-full shrink-0 text-right">
        {getPositionLabel(position)}
      </Text>
      <div className="brand-upload min-h-0 w-full flex-1">
        <FileUpload
          type="image"
          accept=".jpg,.png,.webp"
          disabled={disabled || uploading}
          aria-label={`Upload image for ${brandName}`}
          variant={uploading ? 'uploading' : brand.imageUrl ? 'complete' : 'edit'}
          imageUri={brand.imageUrl}
          onUpdate={file => {
            setFileError('')
            handleUpload(file)
          }}
          onDelete={handleDelete}
          onFileError={setFileError}
          feedbackType={error ? 'error' : 'info'}
          helperText={error || (uploading ? `Image update in progress (${progress}%).` : '')}
          description="Drag and drop your files here (only .jpg, .png or .webp)"
          files={[]}
          maxFiles={1}
        />
      </div>
      <Text size="xsmall" variant="bold" className="w-full shrink-0 truncate text-center uppercase" title={brandName}>
        {brandName}
      </Text>
    </div>
  )
}

export const BrandItem = memo(BrandItemComponent)
